import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
} from "@tanstack/react-table";
import _ from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { SecuritySuggestionsController } from "../../../api/src/security-suggestions/security-suggestions.controller";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import IndeterminateCheckbox from "../components/IndeterminateCheckbox";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import { textOrExactFilter } from "../components/Table/filters";
import { useTableSettings } from "../components/Table/tableSettings";
import MultiSelector from "../components/MultiSelector";
import { withOverrideMarker } from "../components/OverrideMarker";
import {
  deserializeDate,
  useAuthenticatedMutationAsync,
  useAuthenticatedQuery,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import { permissionAwareErrorNotification } from "../lib/errorNotifications";
import { processFileResponse } from "../lib/file";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import { securityPath, useQuerySecurities } from "../lib/security";
import FirmNav from "./FirmNav";
import SecurityMasterModal from "./SecurityMasterModal";
import {
  ERRORS_OF_ONE_SYMBOL,
  type PendingSuggestion,
  type SuggestionField,
  SuggestedValue,
  SuggestionError,
} from "./SecurityMasterSuggestion";

function useQueryExportSecurityCategoryOverrides() {
  return useAuthenticatedQuery<
    UnpackResponse<FirmsController["exportSecurityAssetCategoryOverrides"]>,
    void
  >({
    queryKey: ["firm", "securities", "asset-categories", "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}

/**
 * The route this screen reads the firm's security asset categories from.
 *
 * Published at `/v1` by APP-1563, in the same change that paginated it. It is
 * also the FIRST element of every react-query key
 * `useAuthenticatedFetchAllPages` uses, so the two `invalidateQueries` calls
 * further down reach every page of the collection rather than only the one
 * whose url they happen to spell.
 */
const SECURITY_ASSET_CATEGORIES_PATH = "/v1/firm/securities/asset-categories";

/**
 * The console-only route that asks Claude for a category and security type
 * per symbol. It saves nothing; accepting a suggestion writes through
 * `SECURITY_ASSET_CATEGORIES_PATH` like the edit modal does.
 */
const SUGGESTIONS_PATH = "/firm/securities/asset-categories/suggestions";

/**
 * The most symbols one suggestion request may name; the route refuses more
 * (`MAX_SUGGESTION_SYMBOLS`, `api/src/security-suggestions/security-suggestions.dto.ts`).
 */
export const MAX_SUGGESTION_SYMBOLS = 50;

type SuggestionsResponse = SerializedObject<
  UnpackResponse<SecuritySuggestionsController["suggestSecurityCategories"]>
>;

type OverrideWrite = {
  category: string;
  cusip?: string;
  description?: string;
  securityType?: string;
  advCategory?: string;
  is13f?: boolean;
  mode: "INSERT" | "DELETE";
};

/**
 * The security category mapping write that accepts `fields` of `suggestion`
 * for `row`. The route upserts every column, so the row's other values are
 * sent back as they are; ADV Category and 13-F are sent only when the firm
 * already overrides them, so accepting a category does not freeze a default
 * into an override.
 */
export function overrideForAcceptedSuggestion(
  row: SecurityMasterRow,
  suggestion: PendingSuggestion,
  fields: SuggestionField[],
): OverrideWrite {
  const category =
    fields.includes("category") && suggestion.suggestedCategory
      ? suggestion.suggestedCategory
      : row.category;
  const securityType =
    fields.includes("securityType") && suggestion.suggestedSecurityType
      ? suggestion.suggestedSecurityType
      : row.securityType;
  return {
    category: category || "",
    cusip: row.cusip || undefined,
    description: row.description || undefined,
    securityType: securityType || undefined,
    advCategory: row.isAdvCategoryOverride
      ? row.advCategory || undefined
      : undefined,
    is13f: row.isIs13fOverride ? (row.is13f ?? undefined) : undefined,
    mode: "INSERT",
  };
}

/** The fields of `suggestion` still waiting for accept or reject. */
function pendingFields(suggestion: PendingSuggestion): SuggestionField[] {
  return [
    ...(suggestion.suggestedCategory ? (["category"] as const) : []),
    ...(suggestion.suggestedSecurityType ? (["securityType"] as const) : []),
  ];
}

/** True when the suggested security type would change the row's current one. */
function securityTypeDiffers(
  row: SecurityMasterRow,
  suggestion: PendingSuggestion,
): boolean {
  return suggestion.suggestedSecurityType !== (row.securityType || "");
}

/**
 * True when `row` shows a suggested value with accept and reject buttons. A
 * suggested security type equal to the current one has no buttons. A row not
 * in hand (the rows are reloading) counts as one with buttons.
 */
function showsValueToDecide(
  row: SecurityMasterRow | undefined,
  suggestion: PendingSuggestion,
): boolean {
  return pendingFields(suggestion).some(
    (field) =>
      field === "category" || !row || securityTypeDiffers(row, suggestion),
  );
}

/** What the route answers with: a page of rows and the pagination beside it. */
type SecurityAssetCategoryResponse = SerializedObject<
  UnpackResponse<FirmsController["getAllSecurityAssetCategoryOverrides"]>
>;

export type SecurityMasterRow = {
  symbol: string;
  description?: string;
  category?: string;
  cusip?: string;
  securityType?: string;
  advCategory?: string;
  is13f?: boolean | null;
  // Sector, industry and country as FastTrack reports them for a stock; null
  // for a fund or a stock with none on record. Not columns of the list, which
  // reads the same for every security: the edit modal shows them, read-only.
  sector?: string | null;
  industry?: string | null;
  country?: string | null;
  updatedTime?: Date | null;
  // Per-field firm-override flags. Display-only: used to prefix the rendered
  // cell with "*"; never part of the sort/filter/export accessor values.
  isCategoryOverride?: boolean;
  isAdvCategoryOverride?: boolean;
  isIs13fOverride?: boolean;
};

/**
 * A column filter shown while the rows a click of "Suggest categories" ticked
 * are in view: empty and disabled, because the click turned the filters off.
 */
function LockedFilter() {
  return (
    <Form.Control
      size="sm"
      type="text"
      disabled
      value=""
      placeholder="Search"
      aria-label="Search, locked until the suggestions are decided or dismissed"
    />
  );
}

const SecurityMaster = () => {
  const roles = useRoles();
  const notificationContext = useContext(NotificationContext);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState<SecurityMasterRow | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null,
  );
  // AI suggestions by symbol. Display-only until accepted.
  const [suggestions, setSuggestions] = useState<
    Record<string, PendingSuggestion>
  >({});
  // Symbols the Admin rejected in this page session: rejected a suggested
  // value, or dismissed a pending suggestion or an error of one symbol. A row
  // whose answer had nothing to accept or reject is recorded the same way.
  // Kept only in memory, so a reload starts empty. Without a selection, these
  // rows are not sent again.
  const [rejectedSymbols, setRejectedSymbols] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // The ticked rows. Held here, not in `useTable`, because after a click of
  // "Suggest categories" the table shows only these rows.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // Set by a click of "Suggest categories" (decision an-decision-01a13c1991e46766).
  // The click ticks the rows it sends and the table shows only the ticked
  // rows. Until the view closes, the ticks, the column filters and the
  // button are locked. The column filters that were on before the click come
  // back, and the ticks clear, when no ticked row shows a value to accept or
  // reject or an error, or on "Dismiss all".
  // `awaitingAnswer` is true from the click until the route answers or fails:
  // until then no row shows anything, and the rows must stay.
  const [suggestionView, setSuggestionView] = useState<{
    columnFiltersBefore: ColumnFiltersState;
    globalFilterBefore: unknown;
    awaitingAnswer: boolean;
  } | null>(null);
  const isSuggestionViewOpen = suggestionView !== null;
  const isAdmin = roles.includes("Admin");

  const [tableSettings] = useTableSettings("security-master", {
    sorting: [{ id: "symbol", desc: false }],
    filters: [],
    pageSize: 40,
  });

  const columnHelper = useMemo(
    () => createColumnHelper<SecurityMasterRow>(),
    [],
  );

  // Fetch security asset categories. The route answers a PAGE, not the whole
  // collection (APP-1563), so one response is never the answer on its own: the
  // walk in `useAuthenticatedFetchAllPages` keeps asking until it has as many
  // rows as the sibling key says the firm has. Nothing in `tsc` can catch
  // getting this wrong — the element type of a page and of the whole array are
  // the same — so `SecurityMaster.pagination.spec.tsx` is what holds it.
  const {
    rows: collectedRows,
    isPendingFirstPage: isPendingFirstSecurityAssetCategoriesPage,
    isError: isErrorSecurityAssetCategories,
    reload: reloadSecurityAssetCategories,
  } = useAuthenticatedFetchAllPages<
    SecurityAssetCategoryResponse["data"][number]
  >(SECURITY_ASSET_CATEGORIES_PATH);

  const securityAssetCategories = useMemo(
    () =>
      collectedRows.map((security) => ({
        ...security,
        updatedTime: deserializeDate(security.updatedTime),
      })),
    [collectedRows],
  );

  // Get all symbols for fetching security details
  const allSymbols = useMemo(
    () => securityAssetCategories.map((security) => security.symbol),
    [securityAssetCategories],
  );

  // Fetch complete security details
  const { data: securities } = useQuerySecurities(allSymbols);
  const securitiesMap = useMemo(
    () => _.keyBy(securities ?? [], "identifier"),
    [securities],
  );

  // Combine data into SecurityMasterRow format
  const tableData = useMemo(() => {
    const rows: SecurityMasterRow[] = [];

    // Add existing securities with category mappings
    securityAssetCategories.forEach((categoryMapping) => {
      const security = securitiesMap[categoryMapping.symbol];
      rows.push({
        symbol: categoryMapping.symbol,
        description: security?.description || "",
        category: categoryMapping.category || "",
        cusip: security?.cusip || "",
        securityType: security?.securityType || "",
        advCategory: categoryMapping.advCategory || security?.advCategory || "",
        is13f:
          categoryMapping.is13f !== undefined
            ? categoryMapping.is13f
            : security?.is13f !== undefined
              ? security.is13f
              : null,
        sector: categoryMapping.sector,
        industry: categoryMapping.industry,
        country: categoryMapping.country,
        updatedTime: categoryMapping.updatedTime,
        isCategoryOverride: categoryMapping.isCategoryOverride ?? false,
        isAdvCategoryOverride: categoryMapping.isAdvCategoryOverride ?? false,
        isIs13fOverride: categoryMapping.isIs13fOverride ?? false,
      });
    });

    return rows;
  }, [securityAssetCategories, securitiesMap]);

  // After a click of "Suggest categories", only the ticked rows, whatever
  // their category becomes; otherwise every row, for the built-in table search.
  const filteredData = useMemo(
    () =>
      suggestionView
        ? tableData.filter((row) => rowSelection[row.symbol])
        : tableData,
    [tableData, suggestionView, rowSelection],
  );

  const rowsBySymbol = useMemo(() => _.keyBy(tableData, "symbol"), [tableData]);

  // Only show the override legend when at least one value is firm-overridden.
  const hasOverrides = useMemo(
    () =>
      tableData.some(
        (row) =>
          row.isCategoryOverride ||
          row.isAdvCategoryOverride ||
          row.isIs13fOverride,
      ),
    [tableData],
  );

  // Category filter options
  const categoryOptions = useMemo(() => {
    const uniqueCategories = new Set<string>();
    tableData.forEach((row) => {
      if (row.category) {
        uniqueCategories.add(row.category);
      }
    });

    const options = Array.from(uniqueCategories)
      .sort()
      .map((category) => ({
        label: category,
        value: category,
      }));

    options.unshift({
      label: "Unmapped (No Category)",
      value: "unmapped",
    });

    return options;
  }, [tableData]);

  const SecurityCategoryFilter = useCallback(
    ({ header }: { header: any }) => {
      const onChange = (values: string[]) => {
        header.column.setFilterValue(values);
      };

      return (
        <MultiSelector
          options={categoryOptions}
          placeholder="All Categories"
          setValue={onChange}
          value={header.column.getFilterValue() ?? []}
        />
      );
    },
    [categoryOptions],
  );

  const categoryFilterFn = useCallback(
    (row: any, columnId: string, filterValue: string[]) => {
      if (!filterValue || filterValue.length === 0) {
        return true;
      }

      const category = row.getValue(columnId);

      if (filterValue.includes("unmapped") && (!category || category === "")) {
        return true;
      }

      return filterValue.some((val) => val !== "unmapped" && val === category);
    },
    [],
  );

  const booleanFilterFn = useCallback(
    (row: any, columnId: string, filterValue: string) => {
      if (!filterValue) {
        return true;
      }

      const value = row.getValue(columnId);
      const displayValue = value ? "Yes" : "No";
      return displayValue.toLowerCase().includes(filterValue.toLowerCase());
    },
    [],
  );

  const updateSecurityAssetCategories = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<FirmsController["updateSecurityAssetCategoryOverrides"]>
    >
  >(
    SECURITY_ASSET_CATEGORIES_PATH,
    async (data: Record<string, OverrideWrite>) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const requestSuggestions = useAuthenticatedMutationAsync<SuggestionsResponse>(
    SUGGESTIONS_PATH,
    async (symbols: string[]) => ({
      method: "POST",
      body: JSON.stringify({ symbols }),
    }),
  );

  const recordRejected = useCallback((symbols: string[]) => {
    if (symbols.length === 0) return;
    setRejectedSymbols((previous) => new Set([...previous, ...symbols]));
  }, []);

  const suggestMutation = useMutation({
    mutationFn: async (symbols: string[]) =>
      (await requestSuggestions(symbols)).data,
    onSuccess: (items) => {
      setSuggestions((previous) => ({
        ...previous,
        ..._.keyBy(items, "symbol"),
      }));
      // An answer with nothing to accept or reject is recorded as a reject,
      // so no later click sends that row again.
      recordRejected(
        items
          .filter(
            (item) =>
              !item.error &&
              !showsValueToDecide(rowsBySymbol[item.symbol], item),
          )
          .map((item) => item.symbol),
      );
      const failed = items.filter((item) => item.error).length;
      notificationContext.pushNotification({
        id: "security-master-suggest",
        header: "Suggestions Ready",
        body:
          failed > 0
            ? `${items.length - failed} of ${items.length} securities have suggestions. Review and accept them below.`
            : "Review the suggested values below and accept or reject each one.",
        variant: failed === items.length ? "warning" : "success",
      });
    },
    onError: (error: unknown) => {
      notificationContext.pushNotification(
        permissionAwareErrorNotification(
          error,
          "security-master-suggest",
          "Failed to Suggest Categories",
          "Category suggestions could not be generated",
          "the Security Master",
        ),
      );
    },
    onSettled: () => {
      setSuggestionView((view) => view && { ...view, awaitingAnswer: false });
    },
  });

  /** Drop `fields` from the pending suggestions of each symbol. */
  const clearSuggestionFields = useCallback(
    (entries: { symbol: string; fields: SuggestionField[] }[]) => {
      setSuggestions((previous) => {
        const next = { ...previous };
        for (const { symbol, fields } of entries) {
          const current = next[symbol];
          if (!current) continue;
          const updated = {
            ...current,
            suggestedCategory: fields.includes("category")
              ? null
              : current.suggestedCategory,
            suggestedSecurityType: fields.includes("securityType")
              ? null
              : current.suggestedSecurityType,
          };
          if (pendingFields(updated).length === 0) {
            delete next[symbol];
          } else {
            next[symbol] = updated;
          }
        }
        return next;
      });
    },
    [],
  );

  const acceptMutation = useMutation({
    mutationFn: async (
      entries: {
        row: SecurityMasterRow;
        suggestion: PendingSuggestion;
        fields: SuggestionField[];
      }[],
    ) => {
      await updateSecurityAssetCategories(
        Object.fromEntries(
          entries.map(({ row, suggestion, fields }) => [
            row.symbol,
            overrideForAcceptedSuggestion(row, suggestion, fields),
          ]),
        ),
      );
      return entries;
    },
    onSuccess: (entries) => {
      clearSuggestionFields(
        entries.map(({ row, fields }) => ({ symbol: row.symbol, fields })),
      );
      queryClient.invalidateQueries({
        queryKey: [SECURITY_ASSET_CATEGORIES_PATH],
      });
      // As after a save: restart the walk, or the screen keeps the pages it
      // collected before the write.
      reloadSecurityAssetCategories();
      queryClient.invalidateQueries({
        queryKey: ["v1", "securities"],
      });
      notificationContext.pushNotification({
        id: "security-master-accept",
        header: "Suggestions Accepted",
        body:
          entries.length === 1
            ? `Security category mapping for ${entries[0].row.symbol} has been updated`
            : `${entries.length} security category mappings have been updated`,
        variant: "success",
      });
    },
    onError: (error: unknown) => {
      notificationContext.pushNotification(
        permissionAwareErrorNotification(
          error,
          "security-master-accept",
          "Failed to Accept Suggestion",
          "The suggested values could not be saved",
          "the Security Master",
        ),
      );
    },
  });

  const { mutate: acceptSuggestions, isPending: isAccepting } = acceptMutation;

  const acceptSuggestion = useCallback(
    (row: SecurityMasterRow, fields: SuggestionField[]) => {
      const suggestion = suggestions[row.symbol];
      if (suggestion) {
        acceptSuggestions([{ row, suggestion, fields }]);
      }
    },
    [suggestions, acceptSuggestions],
  );

  const rejectSuggestion = useCallback(
    (symbol: string, field: SuggestionField) => {
      recordRejected([symbol]);
      clearSuggestionFields([{ symbol, fields: [field] }]);
    },
    [clearSuggestionFields, recordRejected],
  );

  const columns = useMemo(
    () => [
      ...(isAdmin
        ? [
            columnHelper.display({
              id: "select",
              size: 40,
              header: ({ table }) => (
                <IndeterminateCheckbox
                  aria-label="Select all securities"
                  disabled={isSuggestionViewOpen}
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              ),
              cell: ({ row }) => (
                <IndeterminateCheckbox
                  aria-label={`Select ${row.original.symbol}`}
                  disabled={isSuggestionViewOpen}
                  checked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("symbol", {
        header: () => "Symbol",
        // The symbol opens the security's page (APP-1486).
        cell: (info) => (
          <Link to={securityPath(info.getValue())}>{info.getValue()}</Link>
        ),
        // A link from the security's page sets `?symbol=` (APP-1486).
        filterFn: textOrExactFilter,
        enableSorting: true,
      }),
      columnHelper.accessor("description", {
        header: () => "Description",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor("category", {
        header: () => "Category",
        cell: (info) => {
          const row = info.row.original;
          const pending = suggestions[row.symbol];
          return (
            <>
              {withOverrideMarker(
                info.getValue() || "No Category",
                row.isCategoryOverride,
              )}
              {pending?.suggestedCategory && (
                <SuggestedValue
                  symbol={row.symbol}
                  field="category"
                  value={pending.suggestedCategory}
                  differs={pending.suggestedCategory !== (row.category || "")}
                  confidence={pending.confidence}
                  rationale={pending.rationale}
                  acceptDisabled={isAccepting}
                  rejectDisabled={isAccepting}
                  onAccept={() => acceptSuggestion(row, ["category"])}
                  onReject={() => rejectSuggestion(row.symbol, "category")}
                />
              )}
              {pending?.error && <SuggestionError message={pending.error} />}
            </>
          );
        },
        filterFn: categoryFilterFn,
        enableSorting: true,
        meta: {
          filterComponent: SecurityCategoryFilter,
        },
      }),
      columnHelper.accessor("cusip", {
        header: () => "CUSIP",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
      }),
      columnHelper.accessor("securityType", {
        header: () => "Security Type",
        cell: (info) => {
          const row = info.row.original;
          const pending = suggestions[row.symbol];
          const differs =
            !!pending?.suggestedSecurityType &&
            securityTypeDiffers(row, pending);
          return (
            <>
              {info.getValue() || "-"}
              {pending?.suggestedSecurityType && (
                <SuggestedValue
                  symbol={row.symbol}
                  field="securityType"
                  value={pending.suggestedSecurityType}
                  differs={differs}
                  confidence={pending.confidence}
                  rationale={pending.rationale}
                  // The mapping write requires a category; accept one first.
                  acceptDisabled={isAccepting || !row.category}
                  rejectDisabled={isAccepting}
                  // A suggestion equal to the current security type is shown
                  // with no accept or reject.
                  onAccept={
                    differs
                      ? () => acceptSuggestion(row, ["securityType"])
                      : undefined
                  }
                  onReject={
                    differs
                      ? () => rejectSuggestion(row.symbol, "securityType")
                      : undefined
                  }
                />
              )}
            </>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor("advCategory", {
        header: () => "ADV Category",
        cell: (info) =>
          withOverrideMarker(
            info.getValue() || "-",
            info.row.original.isAdvCategoryOverride,
          ),
        enableSorting: true,
      }),
      columnHelper.accessor("is13f", {
        header: () => "13-F",
        cell: (info) => {
          const value = info.getValue();
          const display =
            value === null || value === undefined
              ? "Unknown"
              : value
                ? "Yes"
                : "No";
          return withOverrideMarker(display, info.row.original.isIs13fOverride);
        },
        filterFn: booleanFilterFn,
        enableSorting: true,
      }),
      columnHelper.accessor("updatedTime", {
        header: () => "Updated Time",
        cell: (info) => info.getValue()?.toLocaleDateString() || "-",
        enableSorting: true,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        header: () => "Actions",
        size: 120,
        cell: (info) => (
          <div className="d-flex gap-1">
            <ActionButton
              variant="icon"
              label="Edit"
              icon="/icons/edit.svg"
              onClick={() => handleEdit(info.row.original)}
            />
            <ActionButton
              variant="icon"
              label="Delete"
              icon="/icons/trash.svg"
              onClick={() => handleDelete(info.row.original.symbol)}
            />
          </div>
        ),
      }),
    ],
    [
      columnHelper,
      categoryFilterFn,
      booleanFilterFn,
      SecurityCategoryFilter,
      isAdmin,
      suggestions,
      isAccepting,
      acceptSuggestion,
      rejectSuggestion,
      isSuggestionViewOpen,
    ],
  );

  // While the view is open, each column filter shows locked, so no filter
  // changes the rows that the click ticked.
  const tableColumns = useMemo(
    () =>
      isSuggestionViewOpen
        ? columns.map((column) =>
            column.enableColumnFilter === false
              ? column
              : {
                  ...column,
                  meta: { ...column.meta, filterComponent: LockedFilter },
                },
          )
        : columns,
    [columns, isSuggestionViewOpen],
  );

  // Initialize column filters based on URL parameters
  const initialColumnFilters = useMemo(() => {
    const filters: any[] = [];
    const filterParam = searchParams.get("filter");
    if (filterParam === "unmapped") {
      filters.push({
        id: "category",
        value: ["unmapped"],
      });
    }
    // `?symbol=` shows only that security's row (APP-1486).
    const symbolParam = searchParams.get("symbol");
    if (symbolParam) {
      filters.push({ id: "symbol", value: [symbolParam] });
    }
    return filters;
  }, [searchParams]);

  const { table } = useTable({
    columns: tableColumns,
    data: filteredData,
    getRowId: (row) => row.symbol,
    enableRowSelection: isAdmin,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    initialState: {
      pagination: {
        pageSize: tableSettings.pageSize,
      },
      sorting: tableSettings.sorting,
      columnFilters: initialColumnFilters,
    },
    autoResetAll: false,
  });

  // The symbols "Suggest categories" sends: the selected rows if any,
  // otherwise, while the unmapped filter is on, the rows it leaves visible,
  // in table order, up to the route's cap. A row that still shows a pending
  // suggestion is not sent again, and the cap applies after that. Without a
  // selection, a row that shows an error of one symbol is not sent again
  // either; a row that shows any other error is, and neither is a row the
  // Admin rejected in this page session. While the rows a click ticked show,
  // nothing is sent.
  const categoryFilterValue = table.getColumn("category")?.getFilterValue() as
    string[] | undefined;
  const isUnmappedFilterActive =
    categoryFilterValue?.includes("unmapped") ?? false;
  const selectedSymbols = Object.keys(rowSelection).filter(
    (symbol) => rowSelection[symbol],
  );
  // A suggested security type equal to the current one is not pending.
  const hasPendingSuggestion = (symbol: string) => {
    const suggestion = suggestions[symbol];
    return !!suggestion && showsValueToDecide(rowsBySymbol[symbol], suggestion);
  };
  const hasErrorOfOneSymbol = (symbol: string) => {
    const error = suggestions[symbol]?.error;
    return !!error && ERRORS_OF_ONE_SYMBOL.includes(error);
  };
  const isSentAgainWithoutSelection = (symbol: string) =>
    !hasErrorOfOneSymbol(symbol) && !rejectedSymbols.has(symbol);
  const visibleSymbols = table
    .getPrePaginationRowModel()
    .rows.map((row) => row.original.symbol);
  const selectedVisibleSymbols = table
    .getPrePaginationRowModel()
    .rows.filter((row) => row.getIsSelected())
    .map((row) => row.original.symbol);
  const eligibleSuggestionTargets = (
    suggestionView
      ? []
      : selectedSymbols.length > 0
        ? selectedVisibleSymbols
        : isUnmappedFilterActive
          ? visibleSymbols.filter(isSentAgainWithoutSelection)
          : []
  ).filter((symbol) => !hasPendingSuggestion(symbol));
  const suggestionTargets = eligibleSuggestionTargets.slice(
    0,
    MAX_SUGGESTION_SYMBOLS,
  );

  // Tick exactly the rows the click sends, save and turn off every column
  // filter (the search boxes are column filters), and show only the ticked
  // rows.
  const suggestCategories = () => {
    const symbols = suggestionTargets;
    const { columnFilters, globalFilter } = table.getState();
    setSuggestionView({
      columnFiltersBefore: columnFilters,
      globalFilterBefore: globalFilter,
      awaitingAnswer: true,
    });
    setRowSelection(
      Object.fromEntries(symbols.map((symbol) => [symbol, true])),
    );
    table.setColumnFilters([]);
    table.setGlobalFilter(undefined);
    suggestMutation.mutate(symbols);
  };

  // Clear the ticks and put back the filters and the search that were on
  // before the click, so the next click sends the next rows.
  const closeSuggestionView = useCallback(() => {
    if (!suggestionView) return;
    setRowSelection({});
    table.setColumnFilters(suggestionView.columnFiltersBefore);
    table.setGlobalFilter(suggestionView.globalFilterBefore);
    setSuggestionView(null);
  }, [suggestionView, table]);

  useEffect(() => {
    if (!suggestionView || suggestionView.awaitingAnswer) return;
    const ticked = Object.keys(rowSelection).filter(
      (symbol) => rowSelection[symbol],
    );
    // An error does not keep the rows: it stays on its row after the view
    // closes, and the rules above decide what the next click sends.
    const isPending = (symbol: string) => {
      const suggestion = suggestions[symbol];
      return (
        !!suggestion && showsValueToDecide(rowsBySymbol[symbol], suggestion)
      );
    };
    if (ticked.some(isPending)) return;
    // What else the ticked rows show has no accept or reject: a suggested
    // security type equal to the current one. It goes with the ticks. The
    // row is already recorded: on its answer, or on the reject.
    const shownWithoutButtons = ticked.filter(
      (symbol) => suggestions[symbol] && !suggestions[symbol].error,
    );
    if (shownWithoutButtons.length > 0) {
      setSuggestions((previous) => _.omit(previous, shownWithoutButtons));
    }
    closeSuggestionView();
  }, [
    suggestionView,
    rowSelection,
    suggestions,
    rowsBySymbol,
    closeSuggestionView,
  ]);

  // A row with any other error is not recorded, so the next click sends it.
  const dismissAll = () => {
    recordRejected(
      Object.keys(suggestions).filter(
        (symbol) => hasPendingSuggestion(symbol) || hasErrorOfOneSymbol(symbol),
      ),
    );
    setSuggestions({});
    closeSuggestionView();
  };
  // "Dismiss all" shows while any row shows a pending value or an error.
  const showsAnySuggestion = Object.keys(suggestions).some(
    (symbol) => !!suggestions[symbol].error || hasPendingSuggestion(symbol),
  );

  const handleEdit = (row: SecurityMasterRow) => {
    setEditingRow(row);
    setShowModal(true);
  };

  const handleDelete = (symbol: string) => {
    setShowDeleteConfirm(symbol);
  };

  const handleAddNew = () => {
    setEditingRow(null);
    setShowModal(true);
  };

  const confirmDelete = useMutation({
    mutationFn: async () => {
      if (!showDeleteConfirm) return;

      await updateSecurityAssetCategories({
        [showDeleteConfirm]: {
          category: "",
          cusip: null,
          description: null,
          securityType: null,
          advCategory: null,
          is13f: null,
          mode: "DELETE",
        },
      });
      return showDeleteConfirm;
    },
    onSuccess: (symbol) => {
      // The deleted row loses its suggestion, its error and its tick, also
      // when the reload still returns it. It is not recorded as rejected.
      if (symbol) {
        setSuggestions((previous) => _.omit(previous, symbol));
        setRowSelection((previous) => _.omit(previous, symbol));
      }
      queryClient.invalidateQueries({
        queryKey: [SECURITY_ASSET_CATEGORIES_PATH],
      });
      // The pages already collected describe the collection BEFORE the delete,
      // and the walk stops once it has them all — so invalidating alone would
      // refetch nothing. Dropping them restarts the walk from the first page.
      reloadSecurityAssetCategories();
      // Invalidate securities cache since security details may have changed
      queryClient.invalidateQueries({
        queryKey: ["v1", "securities"],
      });
      setShowDeleteConfirm(null);
      notificationContext.pushNotification({
        id: "security-master-delete",
        header: "Security Deleted",
        body: "Security category mapping has been deleted",
        variant: "success",
      });
    },
    onError: (error: unknown) => {
      // APP-1327: distinguish a 403 (no admin permission) from a genuine
      // delete failure so the user is not told the feature is broken.
      notificationContext.pushNotification(
        permissionAwareErrorNotification(
          error,
          "security-master-delete",
          "Failed to Delete Security",
          "Failed to delete security category mapping",
          "the Security Master",
        ),
      );
    },
  });

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExportSecurityCategoryOverrides();

  const onExport = useCallback(async () => {
    await refetchExport();
  }, [refetchExport]);

  // Only while the FIRST page is outstanding. Every further page turns the hook
  // pending again under a new key, and unmounting the table for each of them
  // would flash the screen through Loading once per page.
  if (isPendingFirstSecurityAssetCategoriesPage) {
    return (
      <TabContainerWithTabs tabs={FirmNav}>
        <Loading />
      </TabContainerWithTabs>
    );
  }

  if (isErrorSecurityAssetCategories) {
    return (
      <TabContainerWithTabs tabs={FirmNav}>
        <Alert variant="danger">Failed to retrieve security master data</Alert>
      </TabContainerWithTabs>
    );
  }

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      <Row>
        <Col>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Export"
                icon="/icons/folded-list.svg"
                onClick={onExport}
                disabled={isLoadingExport}
              />
            </ButtonGroup>
            {isAdmin && (
              <ButtonGroup className="me-3">
                <ActionButton
                  variant="primary"
                  label="Add New"
                  icon="/icons/new.svg"
                  onClick={handleAddNew}
                />
              </ButtonGroup>
            )}
            {isAdmin && (
              <ButtonGroup className="me-3">
                <ActionButton
                  variant="primary"
                  icon="/icons/insights.svg"
                  decorativeIcon
                  onClick={suggestCategories}
                  disabled={
                    suggestionTargets.length === 0 ||
                    suggestMutation.isPending ||
                    isSuggestionViewOpen
                  }
                  title={
                    selectedSymbols.length === 0 && !isUnmappedFilterActive
                      ? "Select securities or filter to unmapped securities first"
                      : suggestionTargets.length === 0
                        ? "No securities left to send"
                        : `Suggest a category and security type for ${suggestionTargets.length} securities`
                  }
                  label={
                    suggestMutation.isPending
                      ? "Suggesting…"
                      : eligibleSuggestionTargets.length >
                          MAX_SUGGESTION_SYMBOLS
                        ? `Suggest categories (${MAX_SUGGESTION_SYMBOLS} of ${eligibleSuggestionTargets.length})`
                        : eligibleSuggestionTargets.length > 0
                          ? `Suggest categories (${eligibleSuggestionTargets.length})`
                          : "Suggest categories"
                  }
                />
              </ButtonGroup>
            )}
            {isAdmin && showsAnySuggestion && (
              <ButtonGroup>
                <ActionButton
                  variant="secondary"
                  label="Dismiss all"
                  icon="/icons/reject.svg"
                  decorativeIcon
                  onClick={dismissAll}
                  disabled={isAccepting}
                />
              </ButtonGroup>
            )}
          </ButtonToolbar>
        </Col>
      </Row>

      <Row>
        <Col>
          <Table table={table} showSearchGroup />
        </Col>
      </Row>

      {hasOverrides && (
        <Row>
          <Col>
            <p className="text-muted small mt-2 mb-0">
              <span aria-hidden="true">*</span> indicates a value set by a
              firm-level override. Unmarked values come from the system default.
            </p>
          </Col>
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(null)}
                />
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the security category mapping
                  for <strong>{showDeleteConfirm}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => confirmDelete.mutate()}
                  disabled={confirmDelete.isPending}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <SecurityMasterModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editingRow={editingRow}
        existingSymbols={tableData.map((row) => row.symbol)}
        sendIs13fOnlyWhenChanged
        listedRows={tableData}
        onSave={() => {
          queryClient.invalidateQueries({
            queryKey: [SECURITY_ASSET_CATEGORIES_PATH],
          });
          // As on the delete above: restart the walk, or the screen keeps the
          // pages it collected before the save.
          reloadSecurityAssetCategories();
          // Invalidate securities cache since security details may have changed
          queryClient.invalidateQueries({
            queryKey: ["v1", "securities"],
          });
          setShowModal(false);
        }}
      />
    </TabContainerWithTabs>
  );
};

export default SecurityMaster;
